import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { CDFTOfflineService, ISyncQueueItem } from './cdft-offline.service';
import { CDFTService } from './cdft.service';

export interface SyncQueueNotification {
    id: string;
    cdftId: string;
    wellName?: string;
    message: string;
    timestamp: number;
    status: 'pending' | 'syncing' | 'success' | 'error';
    payload?: any;
    error?: string;
}

/**
 * CDFT Sync Notification Service
 * Monitors sync queue and provides notifications for pending/syncing/success/error states
 */
@Injectable({ providedIn: 'root' })
export class CDFTSyncNotificationService {
    private readonly _syncQueueCount: BehaviorSubject<number> =
        new BehaviorSubject(0);
    private readonly _syncNotifications: BehaviorSubject<
        SyncQueueNotification[]
    > = new BehaviorSubject<SyncQueueNotification[]>([]);
    private readonly _isSyncing: BehaviorSubject<boolean> = new BehaviorSubject(
        false,
    );

    constructor(
        private readonly offlineService: CDFTOfflineService,
        private readonly cdftService: CDFTService,
    ) {
        // Poll sync queue every 5 seconds to update count and notifications
        interval(5000).subscribe(() => {
            this.refreshSyncQueue();
        });

        // Initial load
        this.refreshSyncQueue();
    }

    get syncQueueCount$(): Observable<number> {
        return this._syncQueueCount.asObservable();
    }

    get syncNotifications$(): Observable<SyncQueueNotification[]> {
        return this._syncNotifications.asObservable();
    }

    get isSyncing$(): Observable<boolean> {
        return this._isSyncing.asObservable();
    }

    /**
     * Refresh sync queue count and notifications
     */
    async refreshSyncQueue(): Promise<void> {
        try {
            const count = await this.offlineService.getSyncQueueCount();
            this._syncQueueCount.next(count);

            const groupedItems =
                await this.offlineService.getSyncQueueGrouped();
            const notifications = groupedItems.map((item) =>
                this.mapToNotification(item),
            );
            this._syncNotifications.next(notifications);
        } catch (error) {
            console.error('Error refreshing sync queue:', error);
        }
    }

    /**
     * Map sync queue item to notification
     */
    private mapToNotification(item: ISyncQueueItem): SyncQueueNotification {
        const displayName = item.wellName || item.cdftId;
        return {
            id: `sync-${item.id}`,
            cdftId: item.cdftId || 'unknown',
            wellName: item.wellName,
            message: item.lastError
                ? `Failed to sync ${displayName}`
                : `Pending sync for ${displayName}`,
            timestamp: item.timestamp,
            status: item.lastError ? 'error' : 'pending',
            payload: item.payload,
            error: item.lastError,
        };
    }
    /**
     * Manually trigger sync for all pending items
     */
    async triggerManualSync(): Promise<void> {
        if (this._isSyncing.value) {
            console.warn('Sync already in progress');
            return;
        }

        this._isSyncing.next(true);
        try {
            await this.cdftService.processSyncQueue();
            await this.refreshSyncQueue();
        } catch (error) {
            console.error('Error during manual sync:', error);
        } finally {
            this._isSyncing.next(false);
        }
    }

    /**
     * Get sync queue item details by CDFT ID
     */
    async getSyncItemDetails(
        cdftId: string,
    ): Promise<ISyncQueueItem | undefined> {
        return await this.offlineService.getLatestSyncQueueByCdftId(cdftId);
    }
}
